// composables/usePostHog.ts
import { provide, inject, type InjectionKey } from 'vue'
import posthog from 'posthog-js'
import type { PostHog, PostHogConfig } from 'posthog-js'

const PostHogSymbol: InjectionKey<PostHog> = Symbol('PostHog')

/**
 * Provides PostHog instance to the Vue application
 * @param apiKey - PostHog project API key
 * @param config - PostHog configuration options
 * @returns PostHog instance
 */
export function usePostHogProvider(
  apiKey: string,
  config: Partial<PostHogConfig> = {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only'
  }
): PostHog {
  const posthogInstance = posthog.init(apiKey, config) as PostHog
  provide<PostHog>(PostHogSymbol, posthogInstance)
  return posthogInstance
}

/**
 * Injects PostHog instance into a component
 * @throws Error if PostHog instance is not provided
 * @returns PostHog instance
 */
export function usePostHog(): PostHog {
  const posthogInstance = inject<PostHog>(PostHogSymbol, null as unknown as PostHog)

  if (!posthogInstance) {
    throw new Error(
      'PostHog has not been provided. Make sure to call usePostHogProvider first.'
    )
  }

  return posthogInstance
}
